import { api } from '@/lib/api-client'
import type {
  IssueDerivedStatus,
  IssueSeverity,
  IssueStatus,
} from '@/shared/constants/issue-status'

// ── Hydrated issue shape returned by GET /api/v1/issues/{id} ──────────────
// Mirrors the lifecycle-v2 backend (issues/get_issue) verbatim. Every
// lifecycle field is explicitly nullable rather than `?` because backend
// returns explicit nulls for these (cleaner FE branching).

/**
 * Known lifecycle-v2 event types. Old two-actor v1 types (resolved_by_*,
 * rejected_by_*) still appear on pre-migration issues — they are kept for
 * audit — so `IssueEvent.event_type` is a plain string and renderers MUST
 * fall back to a generic history row for anything outside this union.
 */
export type IssueEventType =
  | 'raised'
  | 'remark'
  | 'acknowledged'
  | 'completed'
  | 'manual_override'

export interface IssueEventActor {
  id: string
  display_name: string
  role: string
}

export interface IssueEvent {
  id: string
  event_type: string
  actor: IssueEventActor | null
  note: string | null
  /** First photo, kept for back-compat. Prefer photo_urls. */
  photo_url: string | null
  /** Full photo array (v2 complete events carry all proof photos). */
  photo_urls: string[]
  metadata: Record<string, unknown> | null
  created_at: string
}

export interface IssueDetailHod {
  id: string
  display_name: string
  role?: string | null
  email: string | null
  phone: string | null
}

export interface IssueDetailRaisedBy {
  id: string | null
  display_name: string
  role?: string | null
  email?: string | null
  phone?: string | null
}

export interface IssueDetailSection {
  id: string | null
  title: string
}

export interface IssueSiteOfficer {
  id: string
  display_name: string
  role?: string | null
  email?: string | null
  phone?: string | null
}

export interface IssueDetail {
  id: string
  issue_ref: string
  // Top-level convenience copy of site.entity_id — backend includes both
  // for symmetry. Used to scope photo-upload presigned URLs without
  // needing a separate /sites/{id} fetch.
  entity_id: string
  /** STORED lifecycle value only (open | pending | completed). */
  status: IssueStatus
  /** Adds the time-derived overdue states — render badges from this. */
  derived_status?: IssueDerivedStatus
  is_overdue?: boolean
  severity: IssueSeverity
  title: string
  description: string
  photo_url: string | null
  site: {
    id: string
    name: string
    entity_id: string
    /** Literal stored sub-type (e.g. "Boys Hostel"); null when unset. */
    site_type?: string | null
    /** The site's CURRENT in-charge FO — distinct from raised_by. */
    assigned_officer?: IssueSiteOfficer | null
  }
  department: { id: string; name: string; hod: IssueDetailHod | null }
  section: IssueDetailSection | null
  /** Flat ref kept for back-compat — same value as source_survey?.survey_ref
   *  when an issue was raised through a survey, empty string otherwise. */
  survey_ref: string
  /** Set when the issue was raised through a survey. Null for manually-
   *  raised issues. Prefer this over flat survey_ref when both exist. */
  source_survey: {
    id: string
    survey_ref: string
    completed_at: string | null
  } | null
  raised_by: IssueDetailRaisedBy
  raised_at: string
  // ── v2 lifecycle columns — null until that transition happens ────────────
  /** When the HOD acknowledged (open → pending). */
  acknowledged_at: string | null
  /** UUID of the acknowledging user (name lives on the timeline event). */
  acknowledged_by: string | null
  /** The resolution target the HOD committed to at acknowledge time. */
  resolution_eta: string | null
  completed_at: string | null
  completed_by: string | null
  /** Action Taken Report (PDF) attached at completion. */
  atr_document_url: string | null
  events: IssueEvent[]
}

export function getIssueDetail(issueId: string): Promise<IssueDetail> {
  return api.get<IssueDetail>(`/issues/${issueId}`)
}

// ── Acknowledge / Complete — the two HOD lifecycle actions (v2) ───────────
// Both return the new event + updated status — NOT the full issue. The
// mutation hooks invalidate the detail + list caches rather than merging.

export interface IssueLifecycleEventStub {
  id: string
  event_type: string
  note: string | null
  photo_url?: string | null
  created_at: string | null
}

export interface AcknowledgeIssueRequest {
  /** ISO-8601 datetime, must be in the future. */
  resolution_eta: string
  note?: string
}

export interface AcknowledgeIssueResponse {
  id: string
  issue_ref: string
  status: IssueStatus
  acknowledged_at: string | null
  resolution_eta: string
  event: IssueLifecycleEventStub
}

export interface CompleteIssueRequest {
  /** Proof-of-work photo URLs — at least one required. */
  photos: string[]
  /** The uploaded Action Taken Report (PDF) URL — required. */
  atr_document_url: string
  note?: string
}

export interface CompleteIssueResponse {
  id: string
  issue_ref: string
  status: IssueStatus
  completed_at: string | null
  /** DERIVED — completed after its resolution_eta. Never stored. */
  completed_overdue: boolean
  atr_document_url: string
  event: IssueLifecycleEventStub
}

// suppressErrorToast: the modals surface 400/403/409 inline next to the
// form instead of the global toast. Success toasts still come from the
// api client's response interceptor (backend message).
export function acknowledgeIssue(
  issueId: string,
  body: AcknowledgeIssueRequest,
): Promise<AcknowledgeIssueResponse> {
  return api.post<AcknowledgeIssueResponse>(`/issues/${issueId}/acknowledge`, body, {
    suppressErrorToast: true,
  })
}

export function completeIssue(
  issueId: string,
  body: CompleteIssueRequest,
): Promise<CompleteIssueResponse> {
  return api.post<CompleteIssueResponse>(`/issues/${issueId}/complete`, body, {
    suppressErrorToast: true,
  })
}
